import { useCallback } from 'react';
import { useEditableTranslation } from '#common/i18n/edit-mode.tsx';
import { type RecordingModel, useRecordingContext } from './RecordingContext';
import { MobileTakeRow, TakeRow } from './TakeRow';

export type RecordingRow = RecordingModel | { isPlaceholder: true; takes: { isPlaceholder: true; text: string }[] };

interface Props {
    recording: RecordingRow;
    index: number;
}

export function Recording({ recording, index }: Props) {
    const { t } = useEditableTranslation('checkout');
    const { setCheckState, deleteTake } = useRecordingContext();
    const setIsChecked = useCallback(
        (textId: string, isChecked: boolean) => setCheckState(textId, isChecked),
        [ setCheckState ]
    );
    const isPlaceholder = 'isPlaceholder' in recording;
    return (
        <div
            className='md:bg-white md:border md:border-gray-200 md:shadow-sm gap-2 md:gap-0 flex flex-col'
            data-recording-index={index}
        >
            <div className='md:px-4 md:py-3 md:border-b md:border-gray-200 md:bg-gray-50'>
                <h2 className='text-lg font-semibold md:text-gray-900'>{isPlaceholder ? '\u00a0' : recording.name}</h2>
            </div>
            <div className='md:p-4'>
                <div className='overflow-x-auto hidden md:block'>
                    <table className='min-w-full divide-y divide-gray-200'>
                        <thead className='bg-gray-50'>
                            <tr>
                                <th className='px-4 py-3 text-left font-semibold text-gray-700 uppercase tracking-wider'>
                                    {t('columns.version')}
                                </th>
                                <th className='px-4 py-3 text-left font-semibold text-gray-700 uppercase tracking-wider'>
                                    {t('columns.text')}
                                </th>
                                <th className='px-4 py-3 text-left font-semibold text-gray-700 uppercase tracking-wider'>
                                    {t('columns.actions')}
                                </th>
                                <th className='px-4 py-3 text-left font-semibold text-gray-700 uppercase tracking-wider'>
                                    {t('columns.buy')}
                                </th>
                            </tr>
                        </thead>
                        <tbody className='bg-white divide-y divide-gray-200'>
                            {recording.takes.map((take, index) => {
                                const isTakePlaceholder = 'isPlaceholder' in take;
                                return (
                                    <TakeRow
                                        key={isTakePlaceholder ? 'placeholder' : take.takeId}
                                        take={take}
                                        index={index}
                                        setIsChecked={(isChecked) =>
                                            isTakePlaceholder ? undefined : setIsChecked(take.takeId, isChecked)}
                                        onDelete={() => isTakePlaceholder ? undefined : deleteTake(take.takeId)}
                                    />
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className='md:hidden gap-2 flex flex-col'>
                    {recording.takes.map((take, index) => {
                        const isTakePlaceholder = 'isPlaceholder' in take;
                        return (
                            <MobileTakeRow
                                key={isTakePlaceholder ? 'placeholder' : take.takeId}
                                take={take}
                                index={index}
                                setIsChecked={(isChecked) =>
                                    isTakePlaceholder ? undefined : setIsChecked(take.takeId, isChecked)}
                                onDelete={() => isTakePlaceholder ? undefined : deleteTake(take.takeId)}
                            />
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
